import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, HttpError, type OpenInvitation } from '../api';
import { currentLang, errorText, t } from '../i18n';

/**
 * The owner's "Invite a friend" sheet (beta, OWNER_USER_IDS only): make a single-use invitation link, shown this
 * once with Share and Copy, and the open invitations with Revoke. The server keeps only a hash, so a link that
 * was not copied is simply revoked and made again.
 */
/** How long a link lasts, matching the server's `OWNER_INVITE_DAYS` (`server/src/app.ts`). */
const INVITE_DAYS = 7;

@customElement('invite-sheet')
export class InviteSheet extends LitElement {
  @state() private open: OpenInvitation[] = [];
  /** The link just made; gone when the sheet closes or it is revoked. */
  @state() private link = '';
  /** The invitation behind `link`, so revoking it clears the link. */
  private linkId = '';
  @state() private copied = false;
  @state() private busy = false;
  @state() private error = '';

  protected createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  private async load() {
    try {
      this.open = (await api.openInvitations()).invitations;
    } catch (e) {
      this.error = errorText(e);
    }
  }

  private async run(fn: () => Promise<void>) {
    this.busy = true;
    this.error = '';
    try {
      await fn();
    } catch (e) {
      this.error = errorText(e);
    } finally {
      this.busy = false;
    }
  }

  private make = () =>
    this.run(async () => {
      const r = await api.mintInvitation();
      // The server answers with a path only; the link is this page's own origin.
      this.link = `${location.origin}${r.path}`;
      this.linkId = r.id;
      this.copied = false;
      await this.load();
    });

  private revoke = (id: string) =>
    this.run(async () => {
      try {
        await api.revokeInvitation(id);
      } catch (e) {
        // A 404 means it is already gone (revoked or used elsewhere): treat it as success.
        if (!(e instanceof HttpError) || e.status !== 404) throw e;
      } finally {
        if (id === this.linkId) this.clearLink();
        await this.load();
      }
    });

  private clearLink() {
    this.link = '';
    this.linkId = '';
    this.copied = false;
  }

  /** Put the shown link away to make another, without closing the sheet. */
  private another = () => this.clearLink();

  private copy = async () => {
    try {
      await navigator.clipboard.writeText(this.link);
      this.copied = true;
    } catch {
      /* No clipboard here: the link stays on screen to copy by hand. */
    }
  };

  private share = async () => {
    try {
      await navigator.share({ title: t('invite.shareTitle'), text: t('invite.shareText'), url: this.link });
    } catch {
      /* Cancelled, or refused: the link stays on screen. */
    }
  };

  private close = () => this.dispatchEvent(new CustomEvent('invite-close', { bubbles: true }));

  render() {
    const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
    return html`
      <div class="card invite-sheet" role="dialog" aria-labelledby="invite-title" style="margin-bottom:10px">
        <h2 id="invite-title" style="font-size:17px;margin:0 0 8px">${t('invite.title')}</h2>
        <p class="muted small" style="margin:0 0 10px">${t('invite.once', { days: INVITE_DAYS })}</p>
        ${this.link
          ? html`
              <div class="invite-link" style="word-break:break-all;margin-bottom:8px"><bdi dir="ltr">${this.link}</bdi></div>
              <div style="display:flex;gap:8px">
                ${canShare ? html`<button class="btn primary invite-share" style="flex:1" @click=${this.share}>${t('invite.share')}</button>` : nothing}
                <button class="btn invite-copy" style="flex:1" @click=${this.copy}>${this.copied ? t('invite.copied') : t('invite.copy')}</button>
              </div>
              <p class="muted small" style="margin:8px 0 0">${t('invite.shownOnce')}</p>
              <button class="btn ghost block invite-another" style="margin-top:8px" ?disabled=${this.busy} @click=${this.another}>${t('invite.another')}</button>`
          : html`<button class="btn primary block invite-make" ?disabled=${this.busy} @click=${this.make}>${t('invite.make')}</button>`}
        ${this.error ? html`<p class="invite-error" role="alert">${this.error}</p>` : nothing}
        <h3 style="font-size:15px;margin:14px 0 6px">${t('invite.openTitle')}</h3>
        ${this.open.length
          ? html`<ul class="invite-open" style="list-style:none;padding:0;margin:0">
              ${this.open.map((inv) => html`
                <li style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
                  <span class="muted small" style="flex:1">${t('invite.until', { date: fmtDay(inv.expiresAt) })}</span>
                  <button class="btn ghost invite-revoke" ?disabled=${this.busy} @click=${() => this.revoke(inv.id)}>${t('invite.revoke')}</button>
                </li>`)}
            </ul>`
          : html`<p class="muted small invite-none" style="margin:0">${t('invite.none')}</p>`}
        <button class="btn ghost block invite-close" style="margin-top:10px" @click=${this.close}>${t('invite.close')}</button>
      </div>`;
  }
}

/** A date as the interface writes it (Persian digits in Persian, from the locale). */
function fmtDay(iso: string) {
  return new Date(iso).toLocaleDateString(currentLang(), { day: 'numeric', month: 'long' });
}
